import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useCallback, useState } from "react";
import { ExpandableRowContentPanel } from "./ExpandableRowContentPanel";
import { CellData } from "./table";
import { TableCell } from "./tableCell/tableCell";
import {
  RowCursorTooltipState,
  TableRowCursorTooltipPortal,
} from "./TableRowCursorTooltipPortal";

/**
 * Props for the custom table body renderer.
 *
 * @property computedCellData - Pre-computed cell matrix (rows × columns) from the table shell.
 * @property tableDataToRender - Raw row objects passed through on row-click handlers.
 * @property rowClassName - Static class or per-row function applied to each `<tr>`.
 * @property isExpandableRowTable - When true, renders rows with expandable content panels.
 * @property expandableRowContent - Render function for the expanded content below each row.
 * @property onRowExpansionChangeFromTableBody - Fired when a row's expansion state changes.
 * @property sortedTableHeaders - Ordered column headers (used for colspan in expandable mode).
 * @property getRowTooltip - When set, rows that return tooltip content show a cursor tooltip and suppress gray hover bg.
 */
interface TableBodyProps {
  computedCellData: CellData[][];
  tableDataToRender: any[];
  tableWidth: number;
  onRowClick: (tableData: any[], rowIndex: number, className: string) => void;
  onAssignToClick?: (data: any) => void;
  onStatusClick?: (data: any) => void;
  onDeleteIconClick?: (data: any) => void;
  onEditIconClick?: (data: any) => void;
  onIconCellClick?: (data: any) => void;
  onClickableTextWithDisabledStatusClick?: (data: any) => void;
  onComplete?: () => void;
  /** Fired when a CLICKABLE_BADGE_WITH_ON_CLICK cell is clicked; receives the cell `id`. */
  onClickableBadgeClick?: (id: string) => void;
  toggleCheckboxSelection?: (id: string) => void;
  handleDownloadButtonClick?: (id: string) => void;
  onValueChange?: (value: string) => void;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  startIndex?: number;
  onSideModalClose?: () => void;
  onChangeEmit?: (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => void;
  onPropagateEventOnClickInRpaStatusCell?: (id: string) => void;
  onPropagateEventOnClickInRpaStatusTwoCell?: (id: string) => void;
  onRpaStatusForExternalClick?: (
    modalType: "image" | "doc",
    orderId: string,
  ) => void;
  onValueChangeOfTextField?: (value: string, id: string, type?: string) => void;
  onOverlappingIconClick?: (icon_id: string, order_id: string) => void;
  rowClassName?: string | ((rowData: any, rowIndex: number) => string);
  onUploadClick?: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  onSuccessClick?: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  handleIconClickOnDocumentScreenshot?: (rowData: any) => void;
  /** Fired when the user types in a SEARCHABLE_CARD cell's search input. `id` is the cell value's id. */
  onSearchChange?: (searchTerm: string, id: string) => void;
  /** Fired when the user creates a new option in a SEARCHABLE_CARD cell. `id` is the cell value's id. */
  onCreateOption?: (value: string, id: string) => void;
  /** Fired when the user clicks an option in a SEARCHABLE_CARD cell. `id` is the cell value's id. */
  onOptionClick?: (option: SearchableCardOption, id: string) => void;
  isExpandableRowTable?: boolean;
  expandableRowContent?: (rowData: any, rowIndex: number) => React.ReactNode;
  onRowExpansionChangeFromTableBody?: (expanded: boolean, id: string) => void;
  sortedTableHeaders?: any[];
  /**
   * When provided, rows that return tooltip content show a cursor tooltip and
   * suppress the default gray hover background for that row only.
   */
  getRowTooltip?: (rowData: any, rowIndex: number) => React.ReactNode;
}

/** Applied to rows that render a cursor tooltip via {@link TableBodyProps.getRowTooltip}. */
const NO_HOVER_BG_CLASS = "custom-table-row--no-hover-bg";

/**
 * Adds cursor-aligned hover handlers to a data row when tooltips are enabled.
 *
 * @param rowElement - The `<tr>` to augment (include `key` on the element itself)
 * @param tooltipContent - Tooltip body; empty skips handlers
 * @param useRowTooltip - When false, returns the row unchanged
 * @param showTooltip - Shows the shared portaled tooltip at pointer X
 * @param hideTooltip - Hides the shared portaled tooltip
 * @returns The row with optional mouse handlers
 */
const prepareRowWithCursorTooltip = (
  rowElement: React.ReactElement,
  tooltipContent: React.ReactNode | undefined,
  useRowTooltip: boolean,
  showTooltip: (
    content: React.ReactNode,
    clientX: number,
    anchorTop: number,
  ) => void,
  hideTooltip: () => void,
): React.ReactElement => {
  if (!useRowTooltip || !tooltipContent) {
    return rowElement;
  }

  const existingOnMouseMove = rowElement.props.onMouseMove;
  const existingOnMouseLeave = rowElement.props.onMouseLeave;

  return React.cloneElement(rowElement, {
    onMouseMove: (event) => {
      existingOnMouseMove?.(event);
      const anchorTop = event.currentTarget.getBoundingClientRect().top;
      showTooltip(tooltipContent, event.clientX, anchorTop);
    },
    onMouseLeave: (event) => {
      existingOnMouseLeave?.(event);
      hideTooltip();
    },
  });
};

/**
 * Shared cell renderer props forwarded from TableBody to TableCell.
 *
 * @param props - TableBody props subset used for cell rendering
 * @returns Props object for TableCell
 */
const buildTableCellProps = (
  cell: CellData,
  rowIndex: number,
  startIndex: number | undefined,
  props: TableBodyProps,
  handleTableRowUpdate: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void,
  handleValueChange: (value: string) => void,
  onRowExpandChange?: (expanded: boolean, id: string) => void,
) => ({
  type: cell.type,
  id: cell.rowData?.id,
  value: cell.value,
  header: cell.header,
  rowIndex,
  globalRowIndex: startIndex ? startIndex + rowIndex : rowIndex,
  rowData: cell.rowData,
  handleAssignToClick: props.onAssignToClick,
  handleStatusClick: props.onStatusClick,
  handleDeleteIconClick: props.onDeleteIconClick,
  handleEditIconClick: props.onEditIconClick,
  handleIconCellClick: props.onIconCellClick,
  handleClickableTextWithDisabledStatusClick:
    props.onClickableTextWithDisabledStatusClick,
  onComplete: props.onComplete,
  onClickableBadgeClick: props.onClickableBadgeClick,
  toggleCheckboxSelection: props.toggleCheckboxSelection,
  handleDownloadButtonClick: props.handleDownloadButtonClick,
  onTableRowUpdate: handleTableRowUpdate,
  onValueChange: handleValueChange,
  onSideModalClose: props.onSideModalClose,
  onChangeEmit: props.onChangeEmit,
  onPropagateEventOnClickInRpaStatusCell:
    props.onPropagateEventOnClickInRpaStatusCell,
  onPropagateEventOnClickInRpaStatusTwoCell:
    props.onPropagateEventOnClickInRpaStatusTwoCell,
  onRpaStatusForExternalClick: props.onRpaStatusForExternalClick,
  onValueChangeOfTextField: props.onValueChangeOfTextField,
  onOverlappingIconClick: props.onOverlappingIconClick,
  onUploadClick: props.onUploadClick,
  onSuccessClick: props.onSuccessClick,
  handleIconClickOnDocumentScreenshot:
    props.handleIconClickOnDocumentScreenshot,
  onSearchChange: props.onSearchChange,
  onCreateOption: props.onCreateOption,
  onOptionClick: props.onOptionClick,
  onRowExpandChange,
});

/**
 * Renders the `<tbody>` for the custom table, mapping each row of
 * {@link computedCellData} to a flex `<tr>` and delegating cell rendering to
 * {@link TableCell}.
 *
 * Rows use a stable React `key` (`jCodeId`, then `id`, then index fallback)
 * so reordering data (e.g. CPT/J-code rows) does not reuse stale component
 * state from a previous row at the same index.
 *
 * @param props - See {@link TableBodyProps}.
 * @returns The table body element.
 */
const TableBody: React.FC<TableBodyProps> = (props) => {
  const {
    computedCellData,
    tableDataToRender,
    onRowClick,
    onTableRowUpdate,
    startIndex,
    rowClassName,
    expandableRowContent,
    onRowExpansionChangeFromTableBody,
    sortedTableHeaders,
    isExpandableRowTable,
    getRowTooltip,
  } = props;

  const [activeRowTooltip, setActiveRowTooltip] =
    useState<RowCursorTooltipState | null>(null);

  /**
   * Shows the shared row tooltip above the hovered row at the pointer X position.
   *
   * @param content - Tooltip body
   * @param clientX - Pointer X in viewport coordinates
   * @param anchorTop - Row top edge Y in viewport coordinates
   */
  const showRowTooltip = useCallback(
    (content: React.ReactNode, clientX: number, anchorTop: number) => {
      setActiveRowTooltip({ content, clientX, anchorTop });
    },
    [],
  );

  /** Hides the shared row tooltip when the pointer leaves a row. */
  const hideRowTooltip = useCallback(() => {
    setActiveRowTooltip(null);
  }, []);

  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    onTableRowUpdate?.(rowIndex, fieldKey, newValue);
  };

  const handleValueChange = (_value: string) => {};

  /**
   * Returns whether the row at the given page-relative index is expanded.
   *
   * @param rowIndex - Zero-based index within the current page slice
   * @returns True when the row's expandableRowIcon.isExpanded is true
   */
  const isRowExpanded = useCallback(
    (rowIndex: number): boolean => {
      const actualRowIndex = rowIndex + (startIndex ?? 0);
      const rowData = tableDataToRender[actualRowIndex];
      return rowData?.expandableRowIcon?.isExpanded ?? false;
    },
    [tableDataToRender, startIndex],
  );

  /**
   * Resolves the stable React key, row classes, and optional tooltip for a row.
   *
   * @param rowData - First cell's rowData for the current row
   * @param rowIndex - Zero-based index within the current page slice
   * @returns Object with rowKey, extraRowClass, currentRow, tooltipContent, useRowTooltip
   */
  const getRowMeta = (rowData: CellData[] | undefined, rowIndex: number) => {
    const currentRow = rowData?.[0]?.rowData;
    const globalRowIndex = rowIndex + (startIndex ?? 0);
    const rowKey = currentRow?.jCodeId ?? currentRow?.id ?? `row-${rowIndex}`;
    const extraRowClass =
      typeof rowClassName === "function"
        ? rowClassName(currentRow, rowIndex)
        : (rowClassName ?? "");
    const useRowTooltip = Boolean(getRowTooltip);
    const tooltipContent = getRowTooltip?.(currentRow, globalRowIndex);
    const noHoverClass =
      useRowTooltip && tooltipContent ? NO_HOVER_BG_CLASS : "";
    const rowClassNameResolved = `${extraRowClass} ${noHoverClass}`.trim();
    return {
      rowKey,
      extraRowClass: rowClassNameResolved,
      currentRow,
      tooltipContent,
      useRowTooltip,
    };
  };

  const columnCount =
    computedCellData[0]?.length || sortedTableHeaders?.length || 1;

  const renderTableCell = (
    cell: CellData,
    rowIndex: number,
    onRowExpandChange?: (expanded: boolean, id: string) => void,
  ) => (
    <TableCell
      {...buildTableCellProps(
        cell,
        rowIndex,
        startIndex,
        props,
        handleTableRowUpdate,
        handleValueChange,
        onRowExpandChange,
      )}
    />
  );

  return (
    <tbody>
      {!isExpandableRowTable ? (
        <>
          {computedCellData.map((rowData, rowIndex) => {
            const { rowKey, extraRowClass, tooltipContent, useRowTooltip } =
              getRowMeta(rowData, rowIndex);

            return prepareRowWithCursorTooltip(
              <tr
                key={rowKey}
                style={{ display: "flex", width: "100%" }}
                className={extraRowClass}
              >
                {rowData.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`table-cell ${cell.type}__${cell.header}`}
                    style={{
                      width: `${cell?.width}%`,
                    }}
                    onClick={() =>
                      onRowClick(
                        tableDataToRender,
                        rowIndex,
                        `table-cell ${cell.type}__${cell.header}`,
                      )
                    }
                  >
                    {renderTableCell(cell, rowIndex)}
                  </td>
                ))}
              </tr>,
              tooltipContent,
              useRowTooltip,
              showRowTooltip,
              hideRowTooltip,
            );
          })}
        </>
      ) : (
        <>
          {computedCellData.map((rowData, rowIndex) => {
            const { rowKey, extraRowClass, tooltipContent, useRowTooltip } =
              getRowMeta(rowData, rowIndex);
            const actualRowIndex = rowIndex + (startIndex ?? 0);
            const expanded = isRowExpanded(rowIndex);

            const headerRow = prepareRowWithCursorTooltip(
              <tr
                key={rowKey}
                style={{ display: "block", width: "100%" }}
                className={`${expanded ? "expanded-row" : ""} ${extraRowClass}`.trim()}
              >
                <td
                  colSpan={columnCount}
                  style={{
                    padding: 0,
                    border: "none",
                    display: "block",
                    width: "100%",
                  }}
                >
                  <div
                    style={{ display: "flex", width: "100%" }}
                    className={`${expanded ? "single-row--container shadow" : "single-row--container"}`}
                  >
                    {rowData.map((cell, cellIndex) => (
                      <div
                        key={cellIndex}
                        className={`table-cell ${cell.type}__${cell.header} ${expanded ? "expanded" : ""}`}
                        style={{
                          width: `${cell?.width}%`,
                          padding: "0.75rem 0.75rem",
                          textAlign: "left",
                          borderBottom: `${rowIndex === computedCellData.length - 1 ? "none" : "1px solid #e5e7eb"}`,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() =>
                          onRowClick(
                            tableDataToRender,
                            rowIndex,
                            `table-cell ${cell.type}__${cell.header}`,
                          )
                        }
                      >
                        {renderTableCell(
                          cell,
                          rowIndex,
                          (expandedState, id) => {
                            onRowExpansionChangeFromTableBody?.(
                              expandedState,
                              id,
                            );
                          },
                        )}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>,
              tooltipContent,
              useRowTooltip,
              showRowTooltip,
              hideRowTooltip,
            );

            if (!expandableRowContent) {
              return headerRow;
            }

            return [
              headerRow,
              <ExpandableRowContentPanel
                key={`${rowKey}-expand`}
                expanded={expanded}
                columnCount={columnCount}
                accentColor={
                  tableDataToRender[actualRowIndex]?.expandableRowIcon
                    ?.borderColor ?? "transparent"
                }
                borderBottom={
                  rowIndex === computedCellData.length - 1
                    ? "none"
                    : "1px solid #e5e7eb"
                }
              >
                {typeof expandableRowContent === "function" &&
                  expandableRowContent(
                    tableDataToRender[actualRowIndex],
                    actualRowIndex,
                  )}
              </ExpandableRowContentPanel>,
            ];
          })}
        </>
      )}
      <TableRowCursorTooltipPortal tooltip={activeRowTooltip} />
    </tbody>
  );
};

export default TableBody;
